
import React from "react";
import { User } from "@/types";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { formatCurrency } from "@/lib/utils";
import { Link } from "react-router-dom";
import { EditChildForm } from "./EditChildForm";

interface ChildCardProps {
  child: User;
  onRemove: (childId: string) => void;
  onUpdateChild: (childId: string, firstName: string, lastName: string) => Promise<void>;
}

export function ChildCard({ child, onRemove, onUpdateChild }: ChildCardProps) {
  const handleRemove = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onRemove(child.id);
  };

  const getInitials = (name: string, lastName: string) => {
    return `${name[0]}${lastName[0]}`.toUpperCase();
  };

  return (
    <Link to={`/child/${child.id}`}>
      <Card className="hover:shadow-md transition-shadow duration-200">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Avatar className="h-16 w-16">
                <AvatarImage src={child.image} alt={child.firstName} />
                <AvatarFallback className="text-lg">
                  {getInitials(child.firstName, child.lastName)}
                </AvatarFallback>
              </Avatar>
              <div>
                <h3 className="text-lg font-semibold">
                  {child.firstName} {child.lastName}
                </h3>
                <p className="text-sm text-muted-foreground">{child.username}</p>
              </div>
            </div>
            <div onClick={(e) => e.preventDefault()}>
              <EditChildForm child={child} onUpdateChild={onUpdateChild} />
            </div>
          </div>
          <div className="mt-4">
            <p className="text-sm text-muted-foreground">Current Balance</p>
            <p className="text-2xl font-bold text-primary">{formatCurrency(child.balance)}</p>
          </div>
        </CardContent>
        <CardFooter className="flex justify-between">
          <Button variant="outline" asChild>
            <Link to={`/child/${child.id}`}>View Details</Link>
          </Button>
          <Button variant="destructive" size="sm" onClick={handleRemove}>
            Remove
          </Button>
        </CardFooter>
      </Card>
    </Link>
  );
}
